import { useState } from 'react';
import { useReduxState } from 'hooks';
import { localeLanguageCodes, localeNames } from 'shared-locales';
import { Badge, FlagIcon, Link } from 'components/common';
import { Icon } from 'components/common/icons';

import type { GetCompetenciesRes } from 'shared-types/responses';

interface Props {
  competency: GetCompetenciesRes['competencyObjs'][0];
}

const CompetenciesListItem = ({ competency }: Props) => {
  const { messages, theme } = useReduxState();

  const [selectedLocale, setSelectedLocale] = useState('default');

  const descriptionLocales = Object.keys(competency.descriptionLocales || {});
  const titleLocales = Object.keys(competency.nameLocales || {});

  const translatedLocales = [
    ...titleLocales.filter((locale) => competency.nameLocales[locale]),
    ...descriptionLocales.filter(
      (locale) =>
        competency.descriptionLocales[locale] && !titleLocales.includes(locale)
    )
  ];

  return (
    <div className="competency">
      <div className="main">
        <div className="title">
          <Link href={`/users/competencies/${competency.id}`}>
            <div className="title-text">
              {selectedLocale !== 'default' &&
              competency.nameLocales?.[selectedLocale]
                ? competency.nameLocales?.[selectedLocale]
                : competency.name}
            </div>
          </Link>

          {competency.allowUserSelfAdding ? (
            <div
              className="user-addable"
              aria-label={messages.competenciesUsersCanAdd}
              data-tooltip={messages.competenciesUsersCanAdd}
            >
              <Icon icon="user-circle" />
            </div>
          ) : null}
        </div>

        {competency.description ? (
          <div className="description">
            {selectedLocale !== 'default' &&
            competency.descriptionLocales?.[selectedLocale]
              ? competency.descriptionLocales?.[selectedLocale]
              : competency.description}
          </div>
        ) : null}

        {competency.levels?.length ? (
          <div className="item">
            <div className="item-label">{`${messages.commonLevels}:`}</div>

            <div className="levels">
              {competency.levels.map((level) => (
                <Badge
                  key={level.name}
                  isRounded={false}
                  size="medium"
                  text={
                    <div className="level-content">
                      {selectedLocale !== 'default' &&
                      level.nameLocales?.[selectedLocale]
                        ? level.nameLocales?.[selectedLocale]
                        : level.name}

                      {level.description ? (
                        <div
                          className="level-description"
                          aria-label={level.description}
                          data-tooltip={level.description}
                        >
                          <Icon icon="question-circle" />
                        </div>
                      ) : null}
                    </div>
                  }
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {translatedLocales.length ? (
        <div className="item">
          <div className="item-label">
            {`${messages.settingsLanguageVariants}:`}
          </div>
          <div className="translations">
            <button
              className={`translation ${
                selectedLocale === 'default' ? 'active' : ''
              }`}
              aria-label={messages.commonDefault}
              data-tooltip={messages.commonDefault}
              onClick={() => setSelectedLocale('default')}
            >
              <Icon icon="globe-americas" style={{ padding: '0 2px' }} />
            </button>

            {translatedLocales.map((locale) => (
              <button
                className={`translation ${
                  selectedLocale === locale ? 'active' : ''
                }`}
                key={locale}
                aria-label={localeNames[locale]}
                data-tooltip={localeNames[locale]}
                onClick={() => setSelectedLocale(locale)}
              >
                <FlagIcon
                  lang={localeLanguageCodes[locale]}
                  style={{ marginTop: -2 }}
                />
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <style jsx>{`
        .competency {
          padding: 5px 0;
        }
        .title {
          display: flex;
          flex-wrap: wrap;
          font-size: 1.5rem;
          font-weight: 700;
          gap: 8px;
          max-width: 600px;
        }
        .title-text {
          font-weight: 700;
        }
        .user-addable {
          font-size: 1.4rem;
          position: relative;
          top: 2px;
        }
        .description {
          display: flex;
          flex-wrap: wrap;
          font-size: 1.4rem;
          font-weight: 400;
          gap: 5px;
          line-height: 1.4;
          margin-top: 5px;
          max-width: 600px;
        }
        .item {
          align-items: center;
          display: flex;
          gap: 15px;
          margin-top: 12px;
        }
        .item-label {
          font-size: 1.4rem;
          font-weight: 600px;
        }
        .translations {
          display: flex;
          gap: 5px;
        }
        .translation {
          background-color: transparent;
          border: 0;
          border-radius: 5px;
          cursor: pointer;
          outline: 0;
          font-size: 1.4rem;
          padding: 3px 5px;
        }
        .translation.active {
          background-color: ${theme.lightGreyDark};
        }
        .levels {
          display: flex;
          flex-wrap: wrap;
          gap: 5px 10px;
          min-width: 100px;
        }
        .level-content {
          display: flex;
          gap: 5px;
        }
      `}</style>
    </div>
  );
};

export default CompetenciesListItem;
